import InputField from 'components/InputField';
import Constants from 'Constants';
import { ErrorsState } from 'pages/CenterValidation/hooks/useCenterValidation';
import { FormEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type CenterValidationFormData = {
  programCode: string;
  password: string;
  adminCode: string;
};

type CenterValidationDialogProperties = {
  isSubmitting: boolean;
  onSubmitCenterDetails: (data: CenterValidationFormData) => void;
  errors: ErrorsState;
  resetValidation: (type?: string) => void;
};

const TERMS_URL = 'https://cgslate.com/data/Terms&Conditions-CGSlate.html';
const PRIVACY_URL = 'https://cgslate.com/data/Privacy-Policy-CGSlate.txt';

export default function CenterValidationDialog({
  onSubmitCenterDetails,
  errors,
  isSubmitting,
  resetValidation,
}: CenterValidationDialogProperties): ReactElement {
  const [formData, setFormData] = useState<CenterValidationFormData>({
    programCode: '',
    adminCode: '',
    password: '',
  });
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const { t } = useTranslation('centerValidation');
  const isSubmitDisabled =
    Object.values(formData).some((value) => !value) || !agreeToTerms || isSubmitting;
  const { programCode, adminCode } = formData;
  const onSubmit = (event_: FormEvent) => {
    event_.preventDefault();
    onSubmitCenterDetails(formData);
  };

  const onAgreeToTermsChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToTerms(event_.target.checked);
  };
  const onChangeListeners = (field: keyof CenterValidationFormData) => (value: string) => {
    resetValidation(field);
    setFormData((previous) => ({ ...previous, [field]: value }));
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderQRCodeSection = () => (
    <>
      <div className="flex items-center justify-center">
        <button
          type="button"
          className="flex h-[50px] w-[50px] items-center justify-center rounded-md border-[1.53px] border-neutral-grey-2 shadow-[0px_2.04px_7.64px_0px_#386AF659]"
        >
          <img
            src="./icons/qr-icon.svg"
            alt="Scan QR code"
            className="h-6 w-6 object-contain object-center"
          />
        </button>
        <p className="ml-4 text-xs2">{t('scanQrToAutoFill')}</p>
      </div>
      <div className="mb-2 text-center text-xs">{t('or')}</div>
    </>
  );

  const renderTermsAndConditions = () => (
    <div className="mb-4 flex items-start">
      <input
        id="agreeToTerms"
        type="checkbox"
        checked={agreeToTerms}
        onChange={onAgreeToTermsChange}
        className="mr-2 mt-2 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
      />
      <label htmlFor="agreeToTerms" className="mt-2 w-[302px] text-xs text-neutral-grey-3">
        {t('byContinuingAgreeToSwiftPal')}{' '}
        <a target="_blank" href={TERMS_URL} className="text-secondary-blue-3" rel="noreferrer">
          {t('termsOfService')}
        </a>{' '}
        {t('and')}{' '}
        <a target="_blank" href={PRIVACY_URL} className="text-secondary-blue-3" rel="noreferrer">
          {t('userPolicy')}
        </a>
      </label>
    </div>
  );

  const renderInputField = (
    field: keyof CenterValidationFormData,
    label: string,
    type: string = 'text',
    disabled: boolean = false,
    showTogglePassword: boolean = false,
  ) => (
    <InputField
      id={field}
      label={`${label}*`}
      type={type}
      value={formData[field]}
      containerClassName="mb-4"
      onChange={onChangeListeners(field)}
      error={errors?.[field]}
      placeholder={`Enter ${label}`}
      disabled={disabled}
      required
      showTogglePassword={showTogglePassword}
    />
  );

  const renderPasswordField = () => (
    <div className="relative mb-4">
      <hr className="relative right-[5vw] mb-4 mt-8 w-[35vw]" />
      {renderInputField(
        'password',
        'Password',
        'password',
        !(programCode.length > 0 && adminCode.length > 0),
        true,
      )}
    </div>
  );

  return (
    <form onSubmit={onSubmit} className="w-full max-w-[27.5em] rounded-lg bg-white p-8">
      <h2 className="mb-[19px] ml-4 max-w-[91%] text-center text-xl2 font-bold text-primary-blue-3">
        {t('enterDetailsToStartProgram')}
      </h2>
      {/* {renderQRCodeSection()} */}
      {renderInputField('programCode', 'Program Code')}
      {renderInputField('adminCode', 'Admin Code')}
      {renderPasswordField()}
      {renderTermsAndConditions()}
      <button
        type="submit"
        disabled={isSubmitDisabled}
        className={`font-boldshadow-md flex w-full items-center justify-center rounded-[72px]  px-4 py-3 text-xl font-bold  shadow-custom focus:outline-none ${
          isSubmitDisabled
            ? 'cursor-not-allowed bg-primary-yellow-1 text-primary-blue-1'
            : 'bg-primary-yellow-3 text-primary-blue-3 focus:ring-2 focus:ring-blue-500'
        }`}
      >
        {isSubmitting && <img src="./icons/loading-icon.svg" alt="Loading" className="mr-4" />}
        {t('submitText')}
      </button>
      <div className="mt-4 text-center text-[0.625rem] text-neutral-grey-3">
        {t('needHelp')}{' '}
        <a
          target="_blank"
          href={Constants.FAQ_URL}
          className="text-[0.625rem] text-blue-600 underline"
          rel="noreferrer"
        >
          {t('contactCustomerSupport')}
        </a>
      </div>
    </form>
  );
}
